import React, { useEffect, useState } from "react";
import Store, { EmergencyStatus, MonitoringAlert } from "../store/Store";

// emergency functions for super admins (ROLE_SUPER_ADMIN, enforced by the backend as well):
// - block / unblock flight starts (kill switch)
// - reload the DJI FlightHub token without redeploy
// - invalidate all sessions (also the own one -> logout)
// plus the list of security monitoring alerts (e.g. drone airborne without order)

// alert details are stored as "[key] Title: message"
function splitAlert(details: string | null): { title: string; message: string } {
  const text = (details ?? "").replace(/^\[[^\]]*\]\s*/, "");
  const idx = text.indexOf(": ");
  return idx > 0 ? { title: text.slice(0, idx), message: text.slice(idx + 2) } : { title: text, message: "" };
}

const AUDIT_LABELS: Record<string, string> = {
  LOGIN_SUCCESS: "Anmeldung",
  LOGIN_FAILED: "Anmeldung fehlgeschlagen",
  LOGIN_ID_AUSTRIA: "Anmeldung ID Austria",
  LOGOUT: "Abmeldung",
  TWO_FACTOR_ENROLLED: "2FA eingerichtet",
  TWO_FACTOR_VERIFY_FAILED: "2FA-Code falsch",
  TWO_FACTOR_ENROLLMENT_LINK_USED: "2FA-Link verwendet",
  TWO_FACTOR_RESET_BY_USER: "2FA zurückgesetzt (Nutzer)",
  TWO_FACTOR_RESET_BY_ADMIN: "2FA zurückgesetzt (Admin)",
  TWO_FACTOR_LINK_SENT: "2FA-Link versendet",
  USER_CREATED: "Nutzer angelegt",
  USER_UPDATED: "Nutzer geändert",
  USER_EMAIL_CHANGED: "E-Mail geändert",
  USER_PASSWORD_CHANGED: "Passwort geändert",
  USER_ROLES_CHANGED: "Rollen geändert",
  USER_DELETED: "Nutzer gelöscht",
  FLIGHT_STARTS_BLOCKED: "Flugstarts gesperrt",
  FLIGHT_STARTS_UNBLOCKED: "Flugstart-Sperre aufgehoben",
  FLIGHTHUB_TOKEN_RELOADED: "DJI-Token neu geladen",
  ALL_SESSIONS_INVALIDATED: "Alle Sitzungen beendet",
  FLIGHTHUB_FLIGHT_TASK_CREATED: "Flugauftrag an DJI",
  MONITORING_ALERT: "Sicherheitswarnung",
};

function AuditLog({ entries }: { entries: MonitoringAlert[] | null }) {
  if (entries === null) return <p className="text-gray-500">Lade Protokoll...</p>;
  if (entries.length === 0) return <p className="text-gray-500" data-pw="emergency-audit-empty">Keine Einträge.</p>;
  return (
    <div className="overflow-auto max-h-96 border rounded-lg">
      <table className="w-full text-sm" data-pw="emergency-audit">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="text-left p-2">Zeit</th>
            <th className="text-left p-2">Benutzer</th>
            <th className="text-left p-2">Ereignis</th>
            <th className="text-left p-2">Details</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-t align-top">
              <td className="p-2 whitespace-nowrap">{formatDate(e.timestamp)}</td>
              <td className="p-2">{e.username}</td>
              <td className="p-2">{AUDIT_LABELS[e.action] ?? e.action}</td>
              <td className="p-2 text-gray-600 break-all">{e.details ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AlertList({ alerts }: { alerts: MonitoringAlert[] | null }) {
  if (alerts === null) return <p className="text-gray-500">Lade Warnungen...</p>;
  if (alerts.length === 0) return <p className="text-gray-500" data-pw="emergency-alerts-empty">Keine Warnungen.</p>;
  return (
    <ul className="flex flex-col gap-3" data-pw="emergency-alerts">
      {alerts.map((a) => {
        const { title, message } = splitAlert(a.details);
        return (
          <li key={a.id} className="border border-orange-200 bg-orange-50 rounded-lg p-3">
            <p className="font-medium text-gray-800">{title}</p>
            <p className="text-sm text-gray-500 mb-1">{formatDate(a.timestamp)}</p>
            {message && <p className="text-sm text-gray-700">{message}</p>}
          </li>
        );
      })}
    </ul>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "–";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleString("de-AT");
}

function LastAction({ by, at }: { by: string | null | undefined; at: string | null | undefined }) {
  if (!by && !at) return <p className="text-sm text-gray-500 mt-2">Bisher nicht ausgeführt.</p>;
  return (
    <p className="text-sm text-gray-500 mt-2">
      Zuletzt: {formatDate(at)}{by ? ` von ${by}` : ""}
    </p>
  );
}

const ConfirmModal: React.FC<{
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, message, confirmLabel, onCancel, onConfirm }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
    <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <div className="text-gray-700 mb-6">{message}</div>
      <div className="flex justify-end space-x-3">
        <button data-pw="button-emergency-confirm-cancel" onClick={onCancel} className="px-4 py-2 rounded border">
          Abbrechen
        </button>
        <button
          data-pw="button-emergency-confirm"
          onClick={onConfirm}
          className="bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700"
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const EmergencyPage: React.FC = () => {
  const [status, setStatus] = useState<EmergencyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"sessions" | "unblock" | null>(null);
  const [alerts, setAlerts] = useState<MonitoringAlert[] | null>(null);
  const [audit, setAudit] = useState<MonitoringAlert[] | null>(null);
  const isSuperAdmin = Store.isSuperAdmin();

  const loadAlerts = async () => {
    const result = await Store.loadMonitoringAlerts();
    setAlerts(result.ok && Array.isArray(result.data) ? (result.data as MonitoringAlert[]) : []);
  };

  const loadAudit = async () => {
    const result = await Store.loadAuditLog();
    setAudit(result.ok && Array.isArray(result.data) ? (result.data as MonitoringAlert[]) : []);
  };

  const loadStatus = async () => {
    setLoading(true);
    const result = await Store.loadEmergencyStatus();
    if (result.ok && result.data) {
      setStatus(result.data as EmergencyStatus);
      setError(null);
    } else {
      setError(result.errorMessage ?? "Status konnte nicht geladen werden.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadStatus();
    loadAlerts();
    loadAudit();
  }, [isSuperAdmin]);

  const applyResult = (data: any) => {
    if (data && typeof data === "object") setStatus(data as EmergencyStatus);
  };

  const onBlock = async () => {
    if (!reason.trim()) {
      setReasonError("Bitte einen Grund für die Sperre angeben.");
      return;
    }
    setReasonError(null);
    setBusy(true);
    try {
      const result = await Store.blockFlightStarts(reason.trim());
      if (result.ok) {
        applyResult(result.data);
        setReason("");
        Store.setPopupMessage("ok", "Flugstarts gesperrt", "Neue Flugstarts werden abgelehnt, bis die Sperre aufgehoben wird.");
      }
    } finally {
      setBusy(false);
    }
  };

  const onUnblock = async () => {
    setConfirm(null);
    setBusy(true);
    try {
      const result = await Store.unblockFlightStarts();
      if (result.ok) {
        applyResult(result.data);
        Store.setPopupMessage("ok", "Sperre aufgehoben", "Flugstarts sind wieder möglich.");
      }
    } finally {
      setBusy(false);
    }
  };

  const onReloadToken = async () => {
    setBusy(true);
    try {
      const result = await Store.reloadFlightHubToken();
      if (result.ok) {
        applyResult(result.data);
        Store.setPopupMessage("ok", "DJI-Token neu geladen", "Der nächste DJI-Aufruf verwendet den aktuellen Token.");
      }
    } finally {
      setBusy(false);
    }
  };

  const onInvalidateSessions = async () => {
    setConfirm(null);
    setBusy(true);
    try {
      const result = await Store.invalidateAllSessions(); // logs out locally on success
      if (result.ok) {
        Store.setPopupMessage("ok", "Alle Sessions beendet", "Alle Benutzer müssen sich neu anmelden.", 8000);
      }
    } finally {
      setBusy(false);
    }
  };

  const blocked = !!status?.flightStartsBlocked;

  if (!isSuperAdmin) {
    return (
      <div className="mx-4 rounded-2xl p-6">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">Administration</h1>
        <p className="text-gray-600" data-pw="emergency-no-permission">
          Die Administration steht nur Super-Admins zur Verfügung.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-4 rounded-2xl p-6 overflow-auto">
      <h1 className="text-2xl font-semibold text-gray-800 mb-2">Administration</h1>
      <p className="text-gray-600 mb-6 max-w-3xl">
        Notfall-Funktionen, Sicherheitswarnungen und Sicherheitsprotokoll. Alle Aktionen werden mit Benutzer und Zeitpunkt protokolliert.
      </p>

      {loading && !status ? (
        <p className="text-gray-500">Lade Status...</p>
      ) : error && !status ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <div className="flex flex-col gap-6 max-w-3xl">
          {/* ---------------------------------------------------------------- flight starts */}
          <section className="bg-white shadow rounded-xl p-6">
            <div className="flex items-center justify-between gap-4 mb-2">
              <h2 className="text-lg font-semibold">Flugstarts sperren</h2>
              <span
                data-pw="emergency-flight-starts-status"
                className={`py-1 px-3 text-xs font-semibold rounded-full ${blocked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
              >
                {blocked ? "Gesperrt" : "Freigegeben"}
              </span>
            </div>
            <p className="text-gray-600 mb-4">
              Solange die Sperre aktiv ist, lehnt das Backend das Starten neuer Flugaufträge ab.
            </p>

            {blocked ? (
              <>
                <p className="mb-1"><span className="font-medium">Grund:</span> {status?.flightStartsBlockedReason || "–"}</p>
                <LastAction by={status?.flightStartsBlockedBy} at={status?.flightStartsBlockedAt} />
                <button
                  data-pw="button-emergency-unblock"
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition disabled:opacity-50"
                  disabled={busy}
                  onClick={() => setConfirm("unblock")}
                >
                  Sperre aufheben
                </button>
              </>
            ) : (
              <>
                <label className="block text-gray-700 text-sm mb-1" htmlFor="emergency-reason">
                  Grund (Pflichtfeld)
                </label>
                <textarea
                  id="emergency-reason"
                  data-pw="input-emergency-reason"
                  className="border p-2 rounded w-full"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="z. B. Verdacht auf gestohlenen DJI-Token"
                />
                {reasonError && <p className="text-red-600 text-sm mt-1">{reasonError}</p>}
                <LastAction by={status?.flightStartsBlockedBy} at={status?.flightStartsBlockedAt} />
                <button
                  data-pw="button-emergency-block"
                  className="mt-4 bg-red-600 text-white px-4 py-2 rounded-xl shadow hover:bg-red-700 transition disabled:opacity-50"
                  disabled={busy || !reason.trim()}
                  onClick={onBlock}
                >
                  Flugstarts sperren
                </button>
              </>
            )}
          </section>

          {/* ---------------------------------------------------------------- DJI token */}
          <section className="bg-white shadow rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-2">DJI-Token neu laden</h2>
            <p className="text-gray-600 mb-2">
              Lädt den DJI-FlightHub-Token sofort neu aus dem Secret, z. B. nachdem er getauscht wurde. Kein Redeploy nötig.
            </p>
            <LastAction by={status?.flightHubTokenReloadedBy} at={status?.flightHubTokenReloadedAt} />
            <button
              data-pw="button-emergency-reload-token"
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition disabled:opacity-50"
              disabled={busy}
              onClick={onReloadToken}
            >
              DJI-Token neu laden
            </button>
          </section>

          {/* ---------------------------------------------------------------- sessions */}
          <section className="bg-white shadow rounded-xl p-6 border border-red-200">
            <h2 className="text-lg font-semibold mb-2">Alle Sessions beenden</h2>
            <p className="text-gray-600 mb-2">
              Beendet sofort alle aktiven Sitzungen. Alle Benutzer – auch Sie selbst – müssen sich neu anmelden.
            </p>
            <LastAction by={status?.sessionsInvalidatedBy} at={status?.sessionsInvalidatedAt} />
            <button
              data-pw="button-emergency-invalidate-sessions"
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded-xl shadow hover:bg-red-700 transition disabled:opacity-50"
              disabled={busy}
              onClick={() => setConfirm("sessions")}
            >
              Alle Sessions beenden
            </button>
          </section>

          {/* ---------------------------------------------------------------- monitoring alerts */}
          <section className="bg-white shadow rounded-xl p-6">
            <div className="flex items-center justify-between gap-4 mb-2">
              <h2 className="text-lg font-semibold">Sicherheitswarnungen</h2>
              <button
                data-pw="button-emergency-reload-alerts"
                className="text-sm px-3 py-1 rounded border hover:bg-gray-50"
                onClick={loadAlerts}
              >
                Aktualisieren
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Automatische Überwachung der DJI-Nutzung: Drohnen, die ohne Digital-Sky-Auftrag fliegen, Flugstarts
              außerhalb des üblichen Zeitfensters und ungewöhnlich viele Flugaufträge. Warnungen gehen zusätzlich per E-Mail
              an die hinterlegten Empfänger.
            </p>
            <AlertList alerts={alerts} />
          </section>

          {/* ---------------------------------------------------------------- audit log */}
          <section className="bg-white shadow rounded-xl p-6">
            <div className="flex items-center justify-between gap-4 mb-2">
              <h2 className="text-lg font-semibold">Sicherheitsprotokoll</h2>
              <button
                data-pw="button-emergency-reload-audit"
                className="text-sm px-3 py-1 rounded border hover:bg-gray-50"
                onClick={loadAudit}
              >
                Aktualisieren
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Die letzten 200 sicherheitsrelevanten Ereignisse: Anmeldungen, 2FA-Änderungen, Nutzerverwaltung, Notfall-Aktionen und Flugaufträge.
            </p>
            <AuditLog entries={audit} />
          </section>
        </div>
      )}

      {confirm === "sessions" && (
        <ConfirmModal
          title="Alle Sessions beenden?"
          message={
            <>
              <p className="mb-2"><strong>Achtung:</strong> Alle angemeldeten Benutzer werden sofort abgemeldet.</p>
              <p>Auch Ihre eigene Sitzung wird beendet – Sie werden danach zur Anmeldung weitergeleitet.</p>
            </>
          }
          confirmLabel="Alle Sessions beenden"
          onCancel={() => setConfirm(null)}
          onConfirm={onInvalidateSessions}
        />
      )}

      {confirm === "unblock" && (
        <ConfirmModal
          title="Sperre aufheben?"
          message="Danach können wieder Flugaufträge gestartet werden."
          confirmLabel="Sperre aufheben"
          onCancel={() => setConfirm(null)}
          onConfirm={onUnblock}
        />
      )}
    </div>
  );
};

export default EmergencyPage;
